<script setup lang="ts">
// A channel's threads, folded under it in the sidebar (where phones see them too): how many
// replies wait, then the latest few, a search through all of them, and muting.
import { computed, nextTick, ref } from 'vue'
import { useRouter } from 'vue-router'
import { annotations, preview } from '../lib/annotations'
import { displayName, formatTime, threadSummaries, type ThreadSummary } from '../lib/format'
import type { StoredMessage } from '../storage/db'
import { useSession } from '../stores/session'
import { useUnread } from '../stores/unread'
import Icon from './Icon.vue'

const props = defineProps<{ spaceId: string; conv: string; name: string }>()
const session = useSession()
const unread = useUnread()
const router = useRouter()

/** How many threads show, unless searching. */
const SHOWN = 3
interface ThreadRow extends ThreadSummary {
  text: string
  /** Its people and words, lowercased, for the search. */
  haystack: string
  /** Replies from others since I last had the thread open. */
  unread: number
}

const open = ref(false)
const searching = ref(false)
const query = ref('')
const input = ref<HTMLInputElement | null>(null)

// Latest reply on top.
const all = computed<ThreadRow[]>(() => {
  const tree = unread.trees[props.conv]
  if (!tree?.roots.size) return []
  const msgs = session.messages[props.conv] ?? []
  const ann = annotations(msgs, () => false)
  const words = (m: StoredMessage) => `${displayName(session.profiles, m.sender.user_id)} ${preview(m, ann.get(m.envelope.id))}`
  const inThread = new Map<string, string[]>()
  for (const m of msgs) {
    const root = tree.roots.get(m.envelope.id)
    if (root) inThread.set(root, [...(inThread.get(root) ?? []), words(m)])
  }
  return threadSummaries(msgs, tree).map((t) => ({
    ...t,
    text: preview(t.root, ann.get(t.root.envelope.id)),
    haystack: [words(t.root), ...(inThread.get(t.root.envelope.id) ?? [])].join('\n').toLowerCase(),
    unread: unread.threadUnread(props.conv, t.root.envelope.id),
  }))
})
// The latest few, and any thread with new replies however old, so a count in the badge
// is always there to open. Searching looks through every thread here.
const shown = computed(() => {
  const q = query.value.trim().toLowerCase()
  return q ? all.value.filter((t) => t.haystack.includes(q)) : all.value.filter((t, i) => i < SHOWN || t.unread > 0)
})
/** New replies across the channel's threads, for the folded line. */
const waiting = computed(() => all.value.reduce((n, t) => n + t.unread, 0))

/** Folding also closes the search. */
function toggle(): void {
  open.value = !open.value
  searching.value = false
  query.value = ''
}

async function toggleSearch(): Promise<void> {
  searching.value = !searching.value
  query.value = ''
  await nextTick()
  input.value?.focus()
}

const isMuted = (t: ThreadRow) => unread.mutedThreads.has(t.root.envelope.id)
const openThread = (t: ThreadRow) =>
  router.push({ name: 'space', params: { spaceId: props.spaceId, convId: props.conv }, query: { thread: t.root.envelope.id } })
</script>

<template>
  <div v-if="all.length" class="mb-1 ml-8 mr-4 min-w-0" :data-testid="`threads-${name}`">
    <div class="flex items-center gap-1">
      <button
        class="mono flex min-w-0 flex-1 items-center gap-1.5 py-1 text-left text-text-muted hover:text-text-primary"
        :aria-expanded="open"
        :data-testid="`threads-toggle-${name}`"
        @click="toggle"
      >
        <Icon :name="open ? 'down' : 'right'" class="shrink-0" />
        <span class="uppercase">Threads</span>
        <span class="text-text-disabled">{{ all.length }}</span>
      </button>
      <!-- Folded only: open, each thread below carries its own count -->
      <span v-if="waiting && !open" class="badge shrink-0" data-testid="threads-unread">{{ waiting }}</span>
      <button
        v-if="open"
        class="btn-icon shrink-0"
        :class="searching ? 'text-text-primary' : ''"
        title="Search threads"
        aria-label="Search threads"
        :aria-expanded="searching"
        data-testid="thread-search-toggle"
        @click="toggleSearch"
      >
        <Icon name="search" />
      </button>
    </div>

    <template v-if="open">
      <input
        v-if="searching"
        ref="input"
        v-model="query"
        type="search"
        class="input my-1 w-full"
        placeholder="Search threads"
        aria-label="Search threads"
        data-testid="thread-search"
        @keydown.esc="toggleSearch"
      />
      <p v-if="!shown.length" class="mono py-1.5 text-text-muted" data-testid="thread-search-empty">No threads match.</p>
      <div
        v-for="t in shown"
        :key="t.root.envelope.id"
        class="group/thread flex items-start gap-2 border-l border-border py-1.5 pl-3 text-sm"
        :class="isMuted(t) ? 'opacity-50' : ''"
        data-testid="thread"
      >
        <button class="min-w-0 flex-1 text-left" @click="openThread(t)">
          <span
            class="block truncate"
            :class="t.unread ? 'text-text-primary' : 'text-text-muted hover:text-text-primary'"
            data-testid="thread-text"
          >
            {{ t.text }}
          </span>
          <!-- Who started it, under what they said -->
          <span class="mono block truncate text-text-disabled">
            <span class="text-text-muted" data-testid="thread-author">{{ displayName(session.profiles, t.root.sender.user_id) }}</span>
            · {{ t.replies }} {{ t.replies === 1 ? 'reply' : 'replies' }} · {{ formatTime(t.last.envelope.ts || t.last.receivedAt) }}
          </span>
        </button>
        <span v-if="t.unread" class="badge mt-0.5" data-testid="thread-unread">{{ t.unread }}</span>
        <button
          class="mono mt-0.5 w-[6ch] shrink-0 text-right text-text-muted uppercase hover:text-text-primary"
          :class="isMuted(t) ? '' : 'opacity-0 group-hover/thread:opacity-100 focus:opacity-100 [@media(hover:none)]:opacity-100'"
          :title="isMuted(t) ? 'Unmute this thread' : 'Mute this thread: its replies don\'t notify or count as unread on this device'"
          data-testid="thread-mute"
          @click="unread.muteThread(t.root.envelope.id, !isMuted(t))"
        >
          {{ isMuted(t) ? 'Unmute' : 'Mute' }}
        </button>
      </div>
    </template>
  </div>
</template>
